import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Team.css';

const DEPTS = ['Sales','Operations','Finance','Tech','Design','Marketing','Management','Other'];
const ROLES = ['User','Admin','Super Admin'];

function MemberModal({ member, theme, onClose, onSave, isNew }) {
  const [form, setForm] = useState(member || {
    name:'', email:'', password:'', role:'User', dept:'Sales', phone:''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleSubmit = async () => {
    if (!form.name || !form.email) return setError('Name and email are required');
    if (isNew && !form.password) return setError('Password is required for new members');
    if (form.password && form.password.length < 8) return setError('Password must be at least 8 characters');
    setLoading(true); setError('');
    try {
      await onSave(form);
      onClose();
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="tm-overlay" onClick={onClose}>
      <div className="tm-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="tm-modal-hd">
          <span>{isNew ? 'Add Team Member' : 'Edit Member'}</span>
          <div className="tm-modal-close" onClick={onClose}>✕</div>
        </div>
        {error && <div className="tm-modal-err">{error}</div>}
        <div className="tm-modal-body">
          <div className="tm-form-row">
            <div className="tm-form-grp"><label className="tm-lbl">Full name *</label><input className="tm-inp" placeholder="e.g. Lakshmi R" value={form.name} onChange={e=>set('name',e.target.value)}/></div>
            <div className="tm-form-grp"><label className="tm-lbl">Email *</label><input className="tm-inp" type="email" placeholder="employee@company.in" value={form.email} onChange={e=>set('email',e.target.value)} disabled={!isNew}/></div>
          </div>
          <div className="tm-form-row">
            <div className="tm-form-grp"><label className="tm-lbl">{isNew ? 'Password *' : 'New password (leave blank to keep)'}</label><input className="tm-inp" type="password" placeholder="Min 8 characters" value={form.password||''} onChange={e=>set('password',e.target.value)}/></div>
            <div className="tm-form-grp"><label className="tm-lbl">Phone</label><input className="tm-inp" placeholder="+91 XXXXX XXXXX" value={form.phone||''} onChange={e=>set('phone',e.target.value)}/></div>
          </div>
          <div className="tm-form-row">
            <div className="tm-form-grp"><label className="tm-lbl">Department</label>
              <select className="tm-inp tm-sel" value={form.dept||'Sales'} onChange={e=>set('dept',e.target.value)}>
                {DEPTS.map(d=><option key={d}>{d}</option>)}
              </select>
            </div>
            <div className="tm-form-grp"><label className="tm-lbl">Role</label>
              <select className="tm-inp tm-sel" value={form.role||'User'} onChange={e=>set('role',e.target.value)}>
                {ROLES.map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="tm-form-grp">
            <label className="tm-lbl">Job title</label>
            <input className="tm-inp" placeholder="e.g. Sales Executive" value={form.job_title||''} onChange={e=>set('job_title',e.target.value)}/>
          </div>
          <div className="tm-role-info">
            <div className="tm-role-badge" style={{background: form.role==='Super Admin'?'rgba(245,166,35,0.1)':form.role==='Admin'?'rgba(59,130,246,0.1)':'rgba(34,197,94,0.1)', color: form.role==='Super Admin'?'#F5A623':form.role==='Admin'?'#3B82F6':'#22C55E'}}>
              {form.role}
            </div>
            <span className="tm-role-desc">
              {form.role==='Super Admin' && 'Full access — all modules, settings, billing'}
              {form.role==='Admin' && 'Can manage leads, team, projects. No billing access'}
              {form.role==='User' && 'Employee view — tasks, attendance, leave, salary only'}
            </span>
          </div>
        </div>
        <div className="tm-modal-ft">
          <div className="tm-btn-sec" onClick={onClose}>Cancel</div>
          <div className="tm-btn-pri" onClick={handleSubmit}>{loading?'Saving…': isNew?'Add Member':'Save Changes'}</div>
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const { theme } = useTheme();
  const { user }  = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // null | 'add' | member obj
  const [filter, setFilter]   = useState('All');
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const canManage = user.role === 'Super Admin' || user.role === 'Admin';

  useEffect(() => {
    api.getTeam().then(setMembers).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const handleAdd = async (form) => {
    const m = await api.addMember(form);
    setMembers(p=>[...p, m]);
  };

  const handleEdit = async (form) => {
    const payload = { name: form.name, role: form.role, dept: form.dept, phone: form.phone, is_active: true };
    if (form.password) payload.password = form.password;
    const m = await api.updateMember(selected.id, payload);
    setMembers(p=>p.map(x=>x.id===selected.id?{...x,...m}:x));
  };

  const handleDelete = async (id) => {
    await api.deleteMember(id);
    setMembers(p=>p.filter(x=>x.id!==id));
    setDeleteConfirm(null);
    if (selected?.id === id) setSelected(null);
  };

  const filtered = filter === 'All' ? members : members.filter(m => m.dept === filter);
  const depts = ['All', ...new Set(members.map(m=>m.dept).filter(Boolean))];

  return (
    <div className="tm-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="tm-main">
        <Topbar
          title="Team"
          subtitle={`${members.length} members · ${members.filter(m=>m.is_active).length} active today`}
          actions={canManage && (
            <div className="tm-add-btn" onClick={()=>setModal('add')}>+ Add Member</div>
          )}
        />
        <div className="tm-filters">
          {depts.map(d=><div key={d} className={`tm-filter${filter===d?' active':''}`} onClick={()=>setFilter(d)}>{d}</div>)}
        </div>
        {loading ? (
          <div className="tm-loading">Loading team…</div>
        ) : (
          <div className="tm-grid">
            {filtered.map(m=>(
              <div key={m.id} className={`tm-card${selected?.id===m.id?' selected':''}`} onClick={()=>setSelected(s=>s?.id===m.id?null:m)}>
                <div className="tm-card-av" style={{background:`rgba(${m.role==='Super Admin'?'245,166,35':m.role==='Admin'?'59,130,246':'34,197,94'},0.15)`, color: m.role==='Super Admin'?'#F5A623':m.role==='Admin'?'#3B82F6':'#22C55E'}}>
                  {m.name?.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)}
                </div>
                <div className="tm-card-name">{m.name}</div>
                <div className="tm-card-dept">{m.dept}</div>
                <div className="tm-card-role" style={{color: m.role==='Super Admin'?'#F5A623':m.role==='Admin'?'#3B82F6':'#22C55E'}}>{m.role}</div>
                <div className="tm-card-email">{m.email}</div>
                {m.phone && <div className="tm-card-phone">{m.phone}</div>}
                {canManage && (
                  <div className="tm-card-actions" onClick={e=>e.stopPropagation()}>
                    <div className="tm-card-btn" onClick={()=>{ setSelected(m); setModal(m); }}>Edit</div>
                    <div className="tm-card-btn tm-card-del" onClick={()=>setDeleteConfirm(m)}>Remove</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {modal === 'add' && (
        <MemberModal theme={theme} isNew onClose={()=>setModal(null)} onSave={handleAdd}/>
      )}
      {modal && modal !== 'add' && (
        <MemberModal theme={theme} member={modal} onClose={()=>setModal(null)} onSave={handleEdit}/>
      )}

      {deleteConfirm && (
        <div className="tm-overlay" onClick={()=>setDeleteConfirm(null)}>
          <div className="tm-confirm" data-theme={theme} onClick={e=>e.stopPropagation()}>
            <div className="tm-confirm-title">Remove {deleteConfirm.name}?</div>
            <div className="tm-confirm-sub">This will deactivate their account. They won't be able to login.</div>
            <div className="tm-confirm-btns">
              <div className="tm-btn-sec" onClick={()=>setDeleteConfirm(null)}>Cancel</div>
              <div className="tm-btn-danger" onClick={()=>handleDelete(deleteConfirm.id)}>Remove</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
